import { OverAskOfferDetails } from '@bpos/v1/over-ask';
import { OverAskOfferUpdateCommand } from '@cds/push/over-ask-commands';
import { createAction, props } from '@ngrx/store';

import BetslipActions from '../../base/store/actions';
import { PickOddsState } from '../../core/picks/pick-models';
import { OverAskBetslipError, OverAskRejectReason } from '../../model/over-ask/over-ask';
import { QueryPlaceStatusPayload } from '../betplacement/actions';
import { OverAskStartFlowPayload } from './models/over-ask.model';

export class BetslipOverAskActions {
    static readonly ACTION_SCHEMA = `${BetslipActions.ACTION_SCHEMA}/over-ask`;

    private static readonly START_FLOW = `${BetslipOverAskActions.ACTION_SCHEMA}/START_FLOW`;
    private static readonly FINISH_FLOW = `${BetslipOverAskActions.ACTION_SCHEMA}/FINISH_FLOW`;

    private static readonly SUBSCRIBE_TO_PUSH_UPDATES = `${BetslipOverAskActions.ACTION_SCHEMA}/SUBSCRIBE_TO_PUSH_UPDATES`;
    private static readonly RECEIVE_PUSH_UPDATE = `${BetslipOverAskActions.ACTION_SCHEMA}/RECEIVE_PUSH_UPDATE`;

    private static readonly REMOVE_BET = `${BetslipOverAskActions.ACTION_SCHEMA}/REMOVE_BET`;
    private static readonly UNDO_REMOVE_BET = `${BetslipOverAskActions.ACTION_SCHEMA}/UNDO_REMOVE_BET`;
    private static readonly TOGGLE_REMOVE_BET = `${BetslipOverAskActions.ACTION_SCHEMA}/TOGGLE_REMOVE_BET`;
    private static readonly PERMANENT_REMOVE_BET = `${BetslipOverAskActions.ACTION_SCHEMA}/PERMANENT_REMOVE_BET`;

    private static readonly CHANGE_PICK_STATE = `${BetslipOverAskActions.ACTION_SCHEMA}/CHANGE_PICK_STATE`;
    private static readonly ACCEPT_OFFER_TIMEOUT_REMAINING_TIME = `@over-ask/ACCEPT_OFFER_TIMEOUT_REMAINING_TIME`; // Don't use @betslip schema so to skip save to state option

    private static readonly BETTING_OFFER_CHANGED = `@over-ask/BETTING_OFFER_CHANGED`;
    private static readonly ACCEPT_BETTING_OFFER_CHANGES = `@over-ask/ACCEPT_BETTING_OFFER_CHANGES`;

    private static readonly ACCEPT_OFFER = `${BetslipOverAskActions.ACTION_SCHEMA}/ACCEPT_OFFER`;
    private static readonly USER_REQUEST_REJECT_OFFER = `${BetslipOverAskActions.ACTION_SCHEMA}/USER_REQUEST_REJECT_OFFER`;
    private static readonly REJECT_OFFER = `${BetslipOverAskActions.ACTION_SCHEMA}/REJECT_OFFER`;

    private static readonly ADD_ERROR = `${BetslipOverAskActions.ACTION_SCHEMA}/ADD_ERROR`;

    private static readonly TRADER_HAS_COUNTER_OFFER_OFFER = `${BetslipOverAskActions.ACTION_SCHEMA}/TRADER_HAS_COUNTER_OFFER_OFFER`;
    private static readonly TRADER_ACCEPTED_OFFER = `${BetslipOverAskActions.ACTION_SCHEMA}/TRADER_ACCEPTED_OFFER`;

    private static readonly SCHEDULE_GET_STATUS = `${BetslipOverAskActions.ACTION_SCHEMA}/SCHEDULE_GET_STATUS`;
    private static readonly FETCH_STATUS = `${BetslipOverAskActions.ACTION_SCHEMA}/FETCH_STATUS`;

    private static readonly PLACE_BET = `${BetslipOverAskActions.ACTION_SCHEMA}/PLACE_BET`;
    private static readonly GET_PLACE_BET_STATUS = `${BetslipOverAskActions.ACTION_SCHEMA}/GET_PLACE_BET_STATUS`;
    private static readonly PLACE_BET_SUCCEEDED = `${BetslipOverAskActions.ACTION_SCHEMA}/PLACE_BET_SUCCEEDED`;
    private static readonly PLACE_BET_FAILED = `${BetslipOverAskActions.ACTION_SCHEMA}/PLACE_BET_FAILED`;
    private static readonly CHECK_OFFER_STATUS = `${BetslipOverAskActions.ACTION_SCHEMA}/CHECK_OFFER_STATUS`;
    /**
     * Start OverAsk flow; init state
     */
    static startFlow = createAction(BetslipOverAskActions.START_FLOW, props<OverAskStartFlowPayload>());
    /**
     * Setting overask OfferId
     */
    static setOverAskOfferId = createAction(BetslipOverAskActions.START_FLOW, props<{ offerId: string }>());
    /**
     * Finish Over ask flow; close window; clean state
     */
    static finishFlow = createAction(BetslipOverAskActions.FINISH_FLOW);

    /**
     * Subscribe to push updates
     */
    static subscribeToPushUpdates = createAction(BetslipOverAskActions.SUBSCRIBE_TO_PUSH_UPDATES, props<{ offerId: number }>());
    /**
     * Receive a push update from CDS
     */
    static receivePushUpdate = createAction(BetslipOverAskActions.RECEIVE_PUSH_UPDATE, props<{ command: OverAskOfferUpdateCommand }>());

    /**
     * Countdown time, user has to accept the offer.
     */
    static acceptOfferTimeoutRemainingTime = createAction(BetslipOverAskActions.ACCEPT_OFFER_TIMEOUT_REMAINING_TIME, props<{ seconds: number }>());

    /**
     * In over ask we check only if betting offer availability has been changed, so we can use a simple flag that set it.
     */
    static bettingOfferChanged = createAction(BetslipOverAskActions.BETTING_OFFER_CHANGED, props<{ hasChanges: boolean }>());

    /**
     * Is called when multi single bet is in over ask process and some of the bets are not available, so this bet will remove them.
     */
    static acceptBettingOfferChanges = createAction(BetslipOverAskActions.ACCEPT_BETTING_OFFER_CHANGES);

    /**
     * User removes a single bet
     */
    static removeBet = createAction(BetslipOverAskActions.REMOVE_BET, props<{ pickId: string }>());
    /**
     * User undo remove a single bet
     */
    static undoRemoveBet = createAction(BetslipOverAskActions.UNDO_REMOVE_BET, props<{ pickId: string }>());

    /**
     * User toggle remove status of a single bet
     */
    static toggleRemoveBet = createAction(BetslipOverAskActions.TOGGLE_REMOVE_BET, props<{ pickId: string }>());

    /**
     *  Bet is removed permanently from the over ask and betslip (Multi single)
     */
    static permanentRemoveBet = createAction(BetslipOverAskActions.PERMANENT_REMOVE_BET, props<{ pickId: string }>());

    /**
     * Change pick status, open -> locked, open -> closed, locked -> open
     */
    static changePickStatus = createAction(BetslipOverAskActions.CHANGE_PICK_STATE, props<{ pickId: string; state: PickOddsState }>());

    /**
     * User accepts the offer
     */
    static acceptOffer = createAction(BetslipOverAskActions.ACCEPT_OFFER);
    /**
     * User clicks cancel during the review process
     */
    static userRequestRejectOffer = createAction(BetslipOverAskActions.USER_REQUEST_REJECT_OFFER);

    /**
     * User cancels or rejects the over ask process/offer.
     */
    static rejectOffer = createAction(BetslipOverAskActions.REJECT_OFFER, props<{ reason: OverAskRejectReason }>());

    /**
     * Trader has counter offer - go to offer review screen.
     */
    static traderHasCounterOffer = createAction(BetslipOverAskActions.TRADER_HAS_COUNTER_OFFER_OFFER, props<OverAskOfferDetails>());

    /**
     * Trader accepted the offer - update the current offer with what was accepted by trader(OddsAcceptance.Higher OR .Any case).
     */
    static traderAcceptedOffer = createAction(BetslipOverAskActions.TRADER_ACCEPTED_OFFER, props<OverAskOfferDetails>());

    /**
     * Schedule get overask status.
     */
    static scheduleGetStatus = createAction(BetslipOverAskActions.SCHEDULE_GET_STATUS, props<{ offerId: string }>());

    /**
     * Fetch over ask status.
     */
    static fetchStatus = createAction(BetslipOverAskActions.FETCH_STATUS, props<{ offerId: string }>());

    /**
     * Place over ask bet.
     */
    static placeBet = createAction(BetslipOverAskActions.PLACE_BET);

    /**
     * Place over ask bet.
     */
    static getPlaceBetStatus = createAction(BetslipOverAskActions.GET_PLACE_BET_STATUS, props<QueryPlaceStatusPayload>());

    /**
     * Place bet has succeeded -> go to summary screen
     */
    static placeBetSucceeded = createAction(BetslipOverAskActions.PLACE_BET_SUCCEEDED);

    /**
     * Place bet has failed -> go back to betslip and show error.
     */
    static placeBetFailed = createAction(BetslipOverAskActions.PLACE_BET_FAILED);

    /**
     * User cancels or rejects the over ask process/offer.
     */
    static addError = createAction(BetslipOverAskActions.ADD_ERROR, props<{ error: OverAskBetslipError }>());

    static checkOfferStatus = createAction(BetslipOverAskActions.CHECK_OFFER_STATUS);
}
